import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { FaTruck } from 'react-icons/fa'
import { useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'

import Buscar from 'shared/components/Buscar'
import { IValueBuscar } from 'shared/components/Buscar/BuscarComponent.types'
import ContainerPage from 'shared/components/ContainerPageNew'
import Excluir from 'shared/components/Excluir'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import Pagination from 'shared/components/Pagination'
import SnackBar from 'shared/components/SnackBar'
import Table from 'shared/components/Table'
import { ICaminhaoData } from 'shared/components/Table/TableComponent.types'
import { colors } from 'shared/styles/colors'
import ICaminhao from 'modules/caminhao/domain/entities/Caminhao'
import { IActiveOrInactive } from 'modules/caminhao/domain/usecases/ActiveOrInactive'
import { IDeleteCaminhao } from 'modules/caminhao/domain/usecases/DeleteCaminhao'
import { IFindCaminhaoByPlaca } from 'modules/caminhao/domain/usecases/FindCaminhaoByPlaca'
import CaminhaoRoutes from 'modules/caminhao/utils/CaminhaoRoutes'
import { useFrotista } from 'modules/user/hooks/useFrotista'

import DetalhesCaminhao from './Detalhes'
import { StyledGrid } from './styles'

type TProps = {
  findCaminhaoByPlaca: IFindCaminhaoByPlaca
  deleteCaminhao: IDeleteCaminhao
  activeOrInactive: IActiveOrInactive
}

const List: FunctionComponent<TProps> = ({
  findCaminhaoByPlaca,
  deleteCaminhao,
  activeOrInactive,
}) => {
  const history = useHistory()
  const { frotistaId } = useFrotista()
  const inputPlacaCaminhaoRef = useRef(null)
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [tableData, setTableData] = useState<ICaminhaoData[]>()
  const [placa, setPlaca] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [perPage] = useState<number>(10)
  const [totalRecords, setTotalRecords] = useState<number>()

  const [openModalDeletar, setOpenModalDeletar] = useState(false)
  const [idSelecionado, setIdSelecionado] = useState(null)
  const [deletado, setDeletado] = useState(false)

  const [openModalDetalhes, setOpenModalDetahles] = useState(false)
  const [caminhaoSelecionado, setCaminhaoSelecionado] =
    useState<ICaminhaoData>()
  const [situacaoCaminhaoSelecionado, setSituacaoCaminhaoSelecionado] =
    useState(false)

  const handleViewFn = useCallback((caminhao: ICaminhao) => {
    setOpenModalDetahles(true)
    setCaminhaoSelecionado(caminhao)
    setSituacaoCaminhaoSelecionado(caminhao.ativo)
  }, [])

  const handleDeleteFn = useCallback((id: number) => {
    setOpenModalDeletar(true)
    setIdSelecionado(id)
  }, [])

  const fetchCaminhoes = useCallback(
    async (page: number, placa: string) => {
      setLoading(true)
      try {
        const response = await findCaminhaoByPlaca.execute({
          placa: placa,
          page: page,
          per_page: perPage,
          frotista_id: frotistaId,
        })

        const tableDataResponse = response.data.map(item => {
          return {
            ...item,
            handleView: () => handleViewFn(item),
            handleDelete: () => handleDeleteFn(item.id),
          }
        })
        setTableData(tableDataResponse)

        setPlaca(placa)
        setTotalRecords(response.total)
        setLoading(false)
      } catch (e: unknown) {
        if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Erro ao buscar caminhão pela placa')
        }
        setOpenErrorMessage(true)
        setLoading(false)
      }
    },
    [findCaminhaoByPlaca, handleDeleteFn, handleViewFn, perPage, frotistaId],
  )

  const handleSubmit = async (values: IValueBuscar) => {
    setCurrentPage(1)
    setPlaca(values.valueInput)
  }

  useEffect(() => {
    const init = async () => {
      fetchCaminhoes(currentPage, placa)
    }
    init()
  }, [currentPage, placa, fetchCaminhoes, frotistaId])

  const onCloseModal = () => {
    setOpenModalDeletar(false)
    setOpenModalDetahles(false)
    setCaminhaoSelecionado(null)
    setDeletado(false)
    setCurrentPage(1)
    fetchCaminhoes(1, '')
  }

  const onDeleteCaminhao = async (id: number) => {
    setLoading(true)
    try {
      await deleteCaminhao.execute(id)
      setDeletado(true)
      setLoading(false)
    } catch (e: unknown) {
      if (e instanceof Error) {
        setErrorMessage(e.message)
      } else {
        setErrorMessage('Erro ao tentar deletar o caminhão')
      }
      setOpenErrorMessage(true)
      setLoading(false)
      onCloseModal()
    }
  }

  const onChangeSituacaoCaminhao = async (caminhao: ICaminhao) => {
    const situacaoCaminhao = caminhao.ativo
    try {
      caminhao.ativo = !situacaoCaminhao
      await activeOrInactive.execute(caminhao)
      setSituacaoCaminhaoSelecionado(caminhao.ativo)
      setDeletado(true)
    } catch (e: unknown) {
      if (e instanceof Error) {
        setErrorMessage(e.message)
      } else {
        setErrorMessage('Erro ao tentar ativar ou inativar um caminhaão')
      }
      setOpenErrorMessage(true)
      setSituacaoCaminhaoSelecionado(situacaoCaminhao)
      onCloseModal()
    }
  }

  return (
    <>
      <ContainerPage
        breadCrumb="Lista de caminhões"
        showFooterScaffold
        onClickNextButton={() => history.push(CaminhaoRoutes.CREATE)}
        titleNextButton="Adicionar"
        iconButtonNext={FaTruck}
        colorNextButton={`${colors.green}`}
        title="Lista de caminhões"
        titleHeader="Novo caminhão"
      >
        <Grid container>
          <Grid item xs={12}>
            <Buscar
              handleSubmit={handleSubmit}
              inputBuscarRef={inputPlacaCaminhaoRef}
              placeholder="Digite a placa para buscar"
              label="Buscar caminhão"
              loading={loading}
            />
          </Grid>
          <StyledGrid container>
            <Grid item xs={12}>
              <Table data={tableData} type="caminhão" />
            </Grid>
          </StyledGrid>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Pagination
                currentPage={currentPage}
                totalRecords={totalRecords}
                recordsPerPage={perPage}
                setCurrentPage={setCurrentPage}
              />
            </Grid>
          </Grid>
        </Grid>
      </ContainerPage>
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={errorMessage}
        horizontal="right"
      />
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}
      <Excluir
        openModalDeletar={openModalDeletar}
        onCloseModal={onCloseModal}
        title={'Excluir caminhão'}
        subTitle={'Tem certeza que deseja excluir este caminhão da sua lista?'}
        deletado={deletado}
        titleDeletado={'Caminhão excluído!'}
        idSelecionado={idSelecionado}
        onDelete={onDeleteCaminhao}
      />

      <DetalhesCaminhao
        caminhao={caminhaoSelecionado}
        openModal={openModalDetalhes}
        checked={situacaoCaminhaoSelecionado}
        onCloseModal={onCloseModal}
        onChangeSituacaoCaminhao={onChangeSituacaoCaminhao}
      />
    </>
  )
}

export default List
